import React, { useEffect, useRef, useState } from 'react';

import { addDays, parseISO, startOfDay } from 'date-fns';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import { createErrorBanner } from 'components/Banner';
import Dot from 'components/Dot';
import { DropdownItem, DropdownMenu, DropdownToggle } from 'components/Dropdown2';
import { Dropdown, Form } from 'components/HookForm';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar/Snackbar.saga';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import URLS from 'constants/urls';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { bem } from 'helpers/class-name';
import { transformKeysToSnakeCase, transformV2ClientResponse } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone, zonedTimeToUtc } from 'helpers/intl-fns';
import segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';

import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import MobileActionSheet from 'components/MobileActionSheet';
import { VENUE_OPEN_STATUS, VENUE_OPEN_STATUS_MAPPING, VENUE_OPEN_STATUS_OPTIONS } from '../Items/SkuItems.constants';
import { skuVenueOpenStatusMessages as messages } from '../Items/SkuItems.messages';
import { sortSelectedFirst } from '../Items/SkuMenuOverview.helpers';
import { getSkuVenueSource } from '../Items/SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from '../Items/SkuAnalytics.constants';

import { SKU_VENUE_CLOSED_CONFIRMATION_MODAL_NAME } from '../Items/components/SkuVenueClosedConfirmationModal';
import './SkuVenueOpenStatus.scss';

const { block, element } = bem('sku-venue-open-status');
const FORM_NAME = 'sku-venue-open-status-form';

const SkuVenueOpenStatus = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const [isLoading, setIsLoading] = useState(true);
  const [venueOpenStatus, setVenueOpenStatus] = useState(null);
  const [existingDisabledWindow, setExistingDisabledWindow] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const isSubmitting = useRef(false);

  const venue = useSelector(selectActiveVenue);
  const { enabled, hideInSearch, venueId, timezone } = venue;
  const source = getSkuVenueSource(venue);
  const isVenueHidden = !enabled || hideInSearch;
  const currentDayInVenueTimezone = startOfDay(adjustDateForTimezone(new Date(), timezone));
  const nextDayInVenueTimezone = addDays(currentDayInVenueTimezone, 1);

  const handleCloseModal = () => dispatch(closeModal());

  const normalizeDisabledWindowToVenueTimezone = disabledTimeWindow => {
    const { startTime, endTime } = disabledTimeWindow;
    return {
      ...disabledTimeWindow,
      startTime: adjustDateForTimezone(parseISO(startTime), timezone),
      endTime: adjustDateForTimezone(parseISO(endTime), timezone),
    };
  };

  const isWindowActive = (normalizedWindow, currentDate) => {
    return currentDate >= normalizedWindow.startTime && currentDate <= normalizedWindow.endTime;
  };

  const fetchVenueDisabledTimeWindows = async signal => {
    try {
      const response = await httpClient.get(
        URLS.venue.getDisabledTimeWindows({ venueId, endsAfter: new Date().toISOString() }),
        { signal }
      );
      const disabledTimeWindows = transformV2ClientResponse(response);
      const currentDateInVenueTimezone = adjustDateForTimezone(new Date(), timezone);

      const activeWindow = disabledTimeWindows
        .map(normalizeDisabledWindowToVenueTimezone)
        .find(window => isWindowActive(window, currentDateInVenueTimezone));

      if (activeWindow) {
        setVenueOpenStatus(VENUE_OPEN_STATUS.CLOSED);
        setExistingDisabledWindow(activeWindow);
      } else {
        setVenueOpenStatus(VENUE_OPEN_STATUS.OPEN);
      }
    } catch (error) {
      if (signal?.aborted) return;
      reportError(error);
      dispatch(createErrorBanner(messages.venueOpenStatusLoadError));
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  };

  const createVenueDisabledTimeWindow = async (startTime, endTime, description) => {
    const utcStartTime = zonedTimeToUtc(startTime, timezone).toISOString();
    const utcEndTime = zonedTimeToUtc(endTime, timezone).toISOString();
    const payload = transformKeysToSnakeCase({
      startTime: utcStartTime,
      endTime: utcEndTime,
      description,
      venueId,
    });
    await httpClient.post(URLS.venue.createDisabledTimeWindow({ venueId }), payload);
  };

  const openVenueClosedConfirmationModal = () => {
    return new Promise(resolve => {
      const handleCancel = () => resolve(false);
      const handleConfirm = () => resolve(true);

      dispatch(
        openModal(SKU_VENUE_CLOSED_CONFIRMATION_MODAL_NAME, {
          handleCancel,
          handleConfirm,
        })
      );
    });
  };

  const hasRemainingDisabledTimeAfterToday = disabledWindow => {
    return disabledWindow.endTime > nextDayInVenueTimezone;
  };

  const handleSetVenueOpen = async () => {
    if (existingDisabledWindow) {
      const { id, description, endTime } = existingDisabledWindow;
      await httpClient.delete(URLS.venue.deleteDisabledTimeWindow({ venueId, timeWindowId: id }));

      if (hasRemainingDisabledTimeAfterToday(existingDisabledWindow)) {
        await createVenueDisabledTimeWindow(nextDayInVenueTimezone, endTime, description);
      }
    }
  };

  function showCloseSuccessSnackbar() {
    dispatch(createSuccessSnackbar(messages.storeClosedSuccess));
  }

  function showOpenSuccessSnackbar() {
    dispatch(createSuccessSnackbar(messages.storeOpenSuccess));
  }

  function showCloseErrorSnackbar(error) {
    dispatch(
      createErrorSnackbar(
        messages.storeClosedErrorTitle,
        {
          strong: chunks => <strong>{chunks}</strong>,
        },
        undefined,
        getApiErrorSnackbarDescription(error)
      )
    );
  }

  function showOpenErrorSnackbar(error) {
    dispatch(
      createErrorSnackbar(
        messages.storeOpenErrorTitle,
        {
          strong: chunks => <strong>{chunks}</strong>,
        },
        undefined,
        getApiErrorSnackbarDescription(error)
      )
    );
  }

  const handleCloseVenue = async () => {
    const confirmed = await openVenueClosedConfirmationModal();
    if (!confirmed) {
      handleCloseModal();
      return false;
    }

    try {
      await createVenueDisabledTimeWindow(currentDayInVenueTimezone, nextDayInVenueTimezone, 'Closed for the day');
      segment.event(SKU_ANALYTICS_EVENTS.VENUE_CLOSED_FOR_DAY, {
        venueId,
        dateOfClosure: currentDayInVenueTimezone.toISOString(),
        source,
      });
      braze.trackEvent(BRAZE_CUSTOM_EVENTS.ADDED_CLOSED_DATES);
      showCloseSuccessSnackbar();
    } catch (error) {
      reportError(error);
      showCloseErrorSnackbar(error);
    }

    handleCloseModal();
    return true;
  };

  const handleOpenVenue = async () => {
    if (!existingDisabledWindow) {
      return;
    }

    try {
      await handleSetVenueOpen();
      segment.event(SKU_ANALYTICS_EVENTS.VENUE_OPENED, { venueId, source });
      showOpenSuccessSnackbar();
    } catch (error) {
      reportError(error);
      showOpenErrorSnackbar(error);
    }
  };

  const onSubmit = async ({ venueOpenStatusInput }) => {
    if (venueOpenStatusInput === venueOpenStatus || isSubmitting.current) {
      return;
    }

    isSubmitting.current = true;
    try {
      if (venueOpenStatusInput === VENUE_OPEN_STATUS.CLOSED) {
        const shouldRefetch = await handleCloseVenue();
        if (!shouldRefetch) {
          return;
        }
      } else if (venueOpenStatusInput === VENUE_OPEN_STATUS.OPEN) {
        await handleOpenVenue();
      }

      await fetchVenueDisabledTimeWindows();
    } finally {
      isSubmitting.current = false;
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchVenueDisabledTimeWindows(controller.signal);
    return () => controller.abort();
  }, [venueId]);

  if (isMobile) {
    return (
      <div className={block()}>
        <button
          type="button"
          className={element('mobile-toggle')}
          disabled={isVenueHidden || isLoading}
          onClick={() => setIsSheetOpen(true)}>
          {isVenueHidden ? (
            <>
              <Isvg className={element('eye-icon')} src={ICONS.eyeWithCross} />
              <span>{intl.formatMessage(messages.venueStatusHidden)}</span>
            </>
          ) : (
            <>
              <Dot
                className={element('status-marker')}
                color={VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.color}
                largeSize
              />
              <span>
                {VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.message &&
                  intl.formatMessage(messages[VENUE_OPEN_STATUS_MAPPING[venueOpenStatus].message])}
              </span>
            </>
          )}
          {!isVenueHidden && !isLoading && (
            <span className={element('mobile-caret')}>
              <SvgArrow direction="down" theme="dark" />
            </span>
          )}
        </button>
        <MobileActionSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          options={sortSelectedFirst(VENUE_OPEN_STATUS_OPTIONS, venueOpenStatus).map(status => ({
            label: (
              <>
                <Dot className={element('status-marker')} color={VENUE_OPEN_STATUS_MAPPING[status]?.color} largeSize />
                {intl.formatMessage(messages[VENUE_OPEN_STATUS_MAPPING[status]?.message])}
              </>
            ),
            onClick: () => {
              if (status !== venueOpenStatus) {
                onSubmit({ venueOpenStatusInput: status });
              }
            },
          }))}
        />
      </div>
    );
  }

  return (
    <div className={block()}>
      <Form id={FORM_NAME} isLoading={isLoading} onSubmit={onSubmit}>
        {({ handleSubmit }) => {
          return (
            <Dropdown
              className={element('dropdown')}
              name="venueOpenStatusInput"
              disabled={isVenueHidden}
              showMenuCaret>
              {({ onChange }) => {
                return (
                  <>
                    <DropdownToggle>
                      <When predicate={isVenueHidden}>
                        <Isvg className={element('eye-icon')} src={ICONS.eyeWithCross} />
                        <Message message={messages.venueStatusHidden} />
                      </When>
                      <When predicate={!isVenueHidden}>
                        <Dot
                          className={element('status-marker')}
                          color={VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.color}
                          largeSize
                        />
                        <Message message={messages[VENUE_OPEN_STATUS_MAPPING[venueOpenStatus]?.message]} />
                      </When>
                    </DropdownToggle>
                    <DropdownMenu className={element('dropdown-menu')}>
                      {sortSelectedFirst(VENUE_OPEN_STATUS_OPTIONS, venueOpenStatus).map(status => {
                        const { color, message } = VENUE_OPEN_STATUS_MAPPING[status];
                        return (
                          <DropdownItem
                            key={status}
                            isSelected={venueOpenStatus === status}
                            onClick={() => {
                              onChange(status);
                              handleSubmit(onSubmit)();
                            }}>
                            <Dot className={element('status-marker')} color={color} largeSize />
                            <Message message={messages[message]} />
                          </DropdownItem>
                        );
                      })}
                    </DropdownMenu>
                  </>
                );
              }}
            </Dropdown>
          );
        }}
      </Form>
    </div>
  );
};

export default SkuVenueOpenStatus;
